import React, { useState, useRef, useEffect } from "react";
import { Button, Input, Space, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";

const SkeletonTable = ({
  rowKey,
  columns = [],
  searchingFields = [],
  data = [],
  isLoading,
  pagination = false,
  handleTableChange = () => {},
  onRowClick = () => {},
  index = true,
  stickHeader = false,
  rowSelection,
  scrollY,
  infiniteScroll = false,
  documentCheck = false,
  isCreate,
}) => {
  const searchInput = useRef(null);
  const tableRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [paginationConfig, setPaginationConfig] = useState({
    current: 1,
    pageSize: 10,
  });

  const [tempSelectedKeys, setTempSelectedKeys] = useState(
    rowSelection?.selectedRowKeys || []
  );

  useEffect(() => {
    setTempSelectedKeys(rowSelection?.selectedRowKeys || []);
  }, [rowSelection?.selectedRowKeys, data]);

  const numberColumn = {
    title: "No.",
    dataIndex: "no",
    render: (_, __, index) => {
      const { current, pageSize } = paginationConfig;
      return (current - 1) * pageSize + index + 1;
    },
    width: 80,
    fixed: "left",
  };

  const newColumns = index ? [numberColumn, ...columns] : columns;

  // ---------- Column wise search functionality ------------ //
  const handleSearchInter = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="p-2" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearchInter(selectedKeys, confirm, dataIndex)
          }
          className="block mb-2"
        />
        <Space>
          <Button
            type="primary"
            size="small"
            className="w-[90px]"
            icon={<SearchOutlined />}
            onClick={() => handleSearchInter(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button
            size="small"
            className="w-[90px]"
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
          >
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#0BAFD4" : "#000000",
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value?.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const updatedColumns =
    searchingFields.length > 0
      ? newColumns.map((column) => {
          let array = searchingFields;
          if (array.includes(column.dataIndex)) {
            return {
              ...column,
              ...getColumnSearchProps(column.dataIndex),
            };
          }
          return column;
        })
      : newColumns;

  useEffect(() => {
    if (!infiniteScroll || data.length <= 6) return;
    const tableBody = tableRef.current?.querySelector(".ant-table-body");
    if (!tableBody) return;
    let animationId;
    const scroll = () => {
      tableBody.scrollTop += 1;
      if (tableBody.scrollTop >= tableBody.scrollHeight / 2)
        tableBody.scrollTop = 0;
      animationId = requestAnimationFrame(scroll);
    };
    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [data, infiniteScroll]);

  const documentRowSelections = {
    ...(rowSelection || {}),
    selectedRowKeys: tempSelectedKeys,
    onChange: (newKeys) => {
      setTempSelectedKeys(newKeys);
      rowSelection?.onChange?.(newKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: !(isCreate && record?.is_use === 0), // Disable all checkboxes when isCreate is 0
    }),
  };

  return (
    <div ref={tableRef}>
      <Table
        className="table-responsive scrollbar-hide"
        columns={updatedColumns}
        dataSource={
          infiniteScroll && data?.length > 6 ? [...data, ...data] : data
        }
        loading={isLoading}
        sortDirections={false}
        pagination={pagination}
        onChange={(pagination, filters) => {
          handleTableChange(pagination, filters);
          setPaginationConfig(pagination);
        }}
        onRow={(record) => {
          return {
            onClick: () => onRowClick(record),
            style: { cursor: "pointer" },
          };
        }}
        scroll={
          stickHeader ? { x: "max-content", y: scrollY } : { x: "max-content" }
        }
        sticky={stickHeader}
        // rowSelection={rowSelection || null}
        rowSelection={
          documentCheck ? documentRowSelections : rowSelection || null
        }
        rowKey={rowKey || null}
      />
    </div>
  );
};

export default SkeletonTable;
